import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { type FC, useCallback, useState } from 'react'
import { toast } from 'sonner'

import { Checkbox } from '@/components/checkbox/checkbox'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { updateI485FilingMutation } from '@/lib/queries/business/v1/case-extra/mutations'
import { getCaseExtraInfoQuery } from '@/lib/queries/business/v3/case-extra/queries'
import { cn } from '@/lib/utils'

import { useFindTargetCase } from '../../../hooks/use-find-target-case'

interface Props {
  className?: string
  filed?: boolean
}

export const FileI485: FC<Props> = ({ className, filed: submitted }) => {
  const mutation = useMutation(updateI485FilingMutation)
  const [isUpdating, setIsUpdating] = useState(false)

  const { targetCase } = useFindTargetCase()
  const caseId = targetCase?.case_id

  const handleCheckedChange = useCallback(
    async (checked: boolean) => {
      if (isUpdating || !caseId) {
        return
      }

      setIsUpdating(true)

      try {
        await mutation.mutateAsync({
          caseId,
          visaBulletinSubmitted: checked,
        })

        // NOTE: At the moment, the server takes time to synchronize the data
        // and we need to wait for a while before revalidating the query.
        // This is a workaround to ensure the data is properly updated.
        setTimeout(async () => {
          await queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v4.case.search.index],
          })
        }, 2000)
        await queryClient.invalidateQueries(getCaseExtraInfoQuery(caseId))
      } catch (err) {
        toast.error('Failed to update.')
      } finally {
        setIsUpdating(false)
      }
    },
    [caseId, mutation, isUpdating]
  )

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label className={cn('flex items-center gap-2', 'transition-opacity duration-150', isUpdating && 'opacity-50')}>
        <Checkbox
          className={cn('border-border focus:border-border data-[state=checked]:border-primary')}
          checked={submitted}
          onCheckedChange={handleCheckedChange}
        />
        <span className={cn('select-none text-xs font-medium leading-5 text-slate-500')}>Did you file Form I-485?</span>
      </label>
    </div>
  )
}
